import Constants from './Constants';
import Utils from './Utils';
import WordList from './assets/WordList';

// Scoring a full board runs a few hundred lookups, and the server now does this
// on every submission, so the list is indexed once at load.
const WORDS = new Set(WordList);

// Board representation and scoring, shared by the client and the server.
// The server re-derives every score from the submitted grid, so this module
// must not depend on React or on any browser API.
export default class Scoring {
  static isWord = (word) => {
    return WORDS.has(word.toLowerCase());
  }

  static createEmptyGridArray = (rows, columns) => {
    const gridArray = [];

    for (let i = 0; i < rows; i++) {
      const gridRow = [];
      for (let j = 0; j < columns; j++) {
        gridRow.push([]);
      }

      gridArray.push(gridRow);
    }

    return gridArray;
  }

  static isExpanded = (expansions, row, column) => {
    return expansions.includes("" + row + "," + column);
  }

  static isBonus = (bonusCells, row, column) => {
    return (bonusCells || []).includes("" + row + "," + column);
  }

  // Which cells of one row or column are bonus cells, as indexes into the
  // string array that line is scored from. `wordStart` and `wordLength` count
  // cells rather than characters -- an expanded cell is one cell holding two
  // letters -- so these indexes line up with a word's span directly.
  static getRowBonusIndexes = (gridArray, row, bonusCells) => {
    const indexes = [];
    for (let j = 0; j < gridArray[row].length; j++) {
      if (Scoring.isBonus(bonusCells, row, j)) {
        indexes.push(j);
      }
    }

    return indexes;
  }

  static getColumnBonusIndexes = (gridArray, column, bonusCells) => {
    const indexes = [];
    for (let i = 0; i < gridArray.length; i++) {
      if (Scoring.isBonus(bonusCells, i, column)) {
        indexes.push(i);
      }
    }

    return indexes;
  }

  static expandGridString = (gridString, rows, columns) => {
    const gridArray = [];
    let pointer = 0;
    for (let i = 0; i < rows; i++) {
      const gridRow = [];
      for (let j = 0; j < columns; j++) {
        const contents = [];
        if (gridString.charAt(pointer) === "[") {
          pointer++;
          while (gridString.charAt(pointer) !== "]") {
            if (gridString.charAt(pointer) !== Constants.LETTER_BLANK) {
              contents.push(gridString.charAt(pointer));
            }
            pointer++;
          }
        } else {
          if (gridString.charAt(pointer) !== Constants.LETTER_BLANK) {
            contents.push(gridString.charAt(pointer));
          }
        }

        gridRow.push(contents);
        pointer++;
      }
      gridArray.push(gridRow);
    }

    return gridArray;
  }

  static compressGridArray = (gridArray, expansions) => {
    let result = "";

    for (let i = 0; i < gridArray.length; i++) {
      for (let j = 0; j < gridArray[i].length; j++) {
        let contents = gridArray[i][j].join("");
        if (Scoring.isExpanded(expansions, i, j)) {
          while (contents.length < 2) {
            contents += Constants.LETTER_BLANK;
          }
          contents = "[" + contents + "]";
        } else {
          while (contents.length < 1) {
            contents += Constants.LETTER_BLANK;
          }
        }

        result += contents;
      }
    }

    return result;
  }

  // The cells of one row or column, ordered from an insertion end inwards, so
  // that index 0 is the edge a slide enters from. `from` is "start" for the left
  // of a row or the top of a column, "end" for the right or the bottom.
  static getLineCoordinates = (rows, columns, edge) => {
    const length = edge.line === "row" ? columns : rows;
    const coordinates = [];

    for (let k = 0; k < length; k++) {
      const offset = edge.from === "start" ? k : length - 1 - k;
      coordinates.push(edge.line === "row"
        ? { row: edge.index, column: offset }
        : { row: offset, column: edge.index });
    }

    return coordinates;
  }

  // Every move a slide day offers: either end of every row and every column.
  static getSlideEdges = (rows, columns) => {
    const edges = [];

    for (let i = 0; i < rows; i++) {
      edges.push({ line: "row", index: i, from: "start" });
      edges.push({ line: "row", index: i, from: "end" });
    }
    for (let j = 0; j < columns; j++) {
      edges.push({ line: "column", index: j, from: "start" });
      edges.push({ line: "column", index: j, from: "end" });
    }

    return edges;
  }

  // A slide inserts a letter at one end of a line and pushes what is already
  // there away from that end -- but only as far as the first empty cell, which
  // the shift closes. Nothing is ever pushed off the board, so a slide adds
  // exactly one letter like any other placement: the round count of a slide day
  // is the round count of any other day, and the board still fills to the last
  // cell. A line with no empty cell cannot be pushed at all, and returns null.
  //
  // Returns a new grid; the one passed in is left alone, since callers compare
  // the two.
  // How far along the line a push travels: the index of the first empty cell,
  // counted from the edge inwards, or -1 if the line is full. Everything from
  // the edge up to it moves; everything past it stays. Kept in one place so the
  // board and the coordinates that describe it cannot disagree about the reach
  // of a push.
  static getSlideGap = (gridArray, edge) => {
    const rows = gridArray.length;
    const columns = rows > 0 ? gridArray[0].length : 0;
    const coordinates = Scoring.getLineCoordinates(rows, columns, edge);

    for (let k = 0; k < coordinates.length; k++) {
      const { row, column } = coordinates[k];
      if (gridArray[row][column].length === 0) {
        return k;
      }
    }

    return -1;
  }

  // Where a push sends each letter it moves, as "row,column" -> "row,column".
  // Anything absent from the map stays where it is. Null when the line is full,
  // matching `applySlide`.
  //
  // The board itself does not record which letters the day gave the player, so
  // those are tracked by coordinate -- and a push moves the coordinate out from
  // under them. This is what lets that marking travel with the letter.
  static getSlideShift = (gridArray, edge) => {
    const rows = gridArray.length;
    const columns = rows > 0 ? gridArray[0].length : 0;
    const coordinates = Scoring.getLineCoordinates(rows, columns, edge);
    const gap = Scoring.getSlideGap(gridArray, edge);

    if (gap === -1) {
      return null;
    }

    const shift = {};
    for (let k = 0; k < gap; k++) {
      const from = coordinates[k];
      const to = coordinates[k + 1];
      shift["" + from.row + "," + from.column] = "" + to.row + "," + to.column;
    }

    return shift;
  }

  static applySlide = (gridArray, edge, letter) => {
    const rows = gridArray.length;
    const columns = rows > 0 ? gridArray[0].length : 0;
    const coordinates = Scoring.getLineCoordinates(rows, columns, edge);
    const gap = Scoring.getSlideGap(gridArray, edge);

    if (gap === -1) {
      return null;
    }

    const next = gridArray.map((cells) => cells.map((cell) => cell.slice()));
    for (let k = gap; k > 0; k--) {
      const to = coordinates[k];
      const from = coordinates[k - 1];
      next[to.row][to.column] = gridArray[from.row][from.column].slice();
    }

    const entry = coordinates[0];
    next[entry.row][entry.column] = [letter];

    return next;
  }

  static getRowStringArray = (gridArray, row, expansions) => {
    let rowStringArray = [];
    for (let j=0; j<gridArray[row].length; j++) {
      let contents = gridArray[row][j].join("");
      while (contents.length < (Scoring.isExpanded(expansions, row, j) ? 2 : 1)) {
        contents += "-";
      }

      rowStringArray.push(contents);
    }

    return rowStringArray;
  }

  static getColumnStringArray = (gridArray, column, expansions) => {
    let columnStringArray = [];
    for (let i=0; i<gridArray.length; i++) {
      let contents = gridArray[i][column].join("");
      while (contents.length < (Scoring.isExpanded(expansions, i, column) ? 2 : 1)) {
        contents += "-";
      }

      columnStringArray.push(contents);
    }

    return columnStringArray;
  }

  // How many bonus cells a word spanning these cells runs through. Each one
  // doubles it, so a word crossing two scores four times; a line with no bonus
  // cells crosses none, which is why every board dealt before bonus cells
  // existed still scores exactly as it did.
  static countBonusCells = (bonusIndexes, wordStart, wordLength, lineLength) => {
    let count = 0;

    for (let i = 0; i < bonusIndexes.length; i++) {
      if (Scoring.isInWord(bonusIndexes[i], wordStart, wordLength, lineLength)) {
        count++;
      }
    }

    return count;
  }

  // Whether a cell of a line falls inside the word spanning `wordLength` cells
  // from `wordStart`. Measured around the line rather than along it, because on
  // a wraparound day a word can run off the end and continue from the start --
  // so `wordStart + wordLength` may exceed the line's length. A word that does
  // not wrap gives the same answer either way, which is why nothing about this
  // needs to know whether the day wraps.
  static isInWord = (index, wordStart, wordLength, lineLength) => {
    if (wordStart < 0 || wordLength <= 0) {
      return false;
    }

    return (index - wordStart + lineLength) % lineLength < wordLength;
  }

  // Where a cell sits in its line's scoring word, for the board to draw. The
  // same test the bonus count uses, so the highlight and the score cannot
  // disagree about which cells a wrapped word covers.
  static getWordPosition = (index, wordStart, wordLength, lineLength) => {
    if (!Scoring.isInWord(index, wordStart, wordLength, lineLength)) {
      return Constants.WORD_POSITION_NONE;
    }
    if (index === wordStart) {
      return Constants.WORD_POSITION_START;
    }
    if (index === (wordStart + wordLength - 1) % lineLength) {
      return Constants.WORD_POSITION_END;
    }

    return Constants.WORD_POSITION_MIDDLE;
  }

  // The scoring word of one line, and what it is worth: the best-scoring valid
  // word, then the longest of those, then the first one this scan reaches.
  //
  // Score rather than length, because until bonus cells there was no difference
  // -- SCORE_TABLE rises strictly with length, so the longest word was always
  // the best-scoring one, and on a line with no bonus cells it still is. Gold
  // separates the two, and choosing by length there would let a line's score
  // *fall* when a player completes a longer word that misses the gold, which is
  // the one thing the board must never do to somebody for building a word.
  //
  // Length remains the tie-break, which is what settles the genuine ties: a
  // doubling is worth exactly one letter at three and four letters, so a
  // doubled three ties a plain four and the longer word takes it.
  //
  // On a wraparound day the line is a loop: a word may start at any cell and
  // run off the end onto the start, so every start is tried at every width. A
  // line with no wrap is scanned exactly as it always was -- the same bounds in
  // the same order, so the first-reached tie-break and every board on record
  // rescore as they did.
  static calculateDataForStringArray = (stringArray, bonusIndexes = [], wraparound = false) => {
    const lineLength = stringArray.length;
    let bestLength = 0;
    let data = {
      wordStart: -1,
      wordLength: -1,
      score: 0
    };

    for (let cellCount=lineLength; cellCount>1; cellCount--) {
      const lastStart = wraparound ? lineLength - 1 : lineLength - cellCount;
      for (let start=0; start<=lastStart; start++) {
        const token = start + cellCount <= lineLength
          ? stringArray.slice(start, start + cellCount).join("")
          : stringArray.slice(start).concat(stringArray.slice(0, start + cellCount - lineLength)).join("");
        if (token.length < Constants.MINIMUM_WORD_LENGTH) {
          continue;
        }

        if (!Scoring.isWord(token)) {
          continue;
        }

        const score = (Constants.SCORE_TABLE[token.length] || 0)
          * Math.pow(Constants.BONUS_CELL_MULTIPLIER, Scoring.countBonusCells(bonusIndexes, start, cellCount, lineLength));

        // Compared on characters, not on cells: an expanded cell holds two
        // letters, so the longer word is not always the wider span.
        if (score < data.score || (score === data.score && token.length <= bestLength)) {
          continue;
        }

        data = {
          wordStart: start,
          wordLength: cellCount,
          score
        };
        bestLength = token.length;
      }
    }

    return data;
  }

  // Scores every row and column of a board in one pass. `bonusCells` and
  // `wraparoundMode` are optional, so a caller replaying a board dealt before
  // either existed scores it exactly as it was scored then.
  static calculateBoardData = (gridArray, expansions, bonusCells = [], wraparoundMode = false) => {
    const rows = gridArray.length;
    const columns = rows > 0 ? gridArray[0].length : 0;

    const rowData = [];
    for (let i = 0; i < rows; i++) {
      rowData.push(Scoring.calculateDataForStringArray(
        Scoring.getRowStringArray(gridArray, i, expansions),
        Scoring.getRowBonusIndexes(gridArray, i, bonusCells),
        wraparoundMode
      ));
    }

    const columnData = [];
    for (let j = 0; j < columns; j++) {
      columnData.push(Scoring.calculateDataForStringArray(
        Scoring.getColumnStringArray(gridArray, j, expansions),
        Scoring.getColumnBonusIndexes(gridArray, j, bonusCells),
        wraparoundMode
      ));
    }

    return {
      rowData,
      columnData,
      totalScore: Utils.calculateTotalScore(rowData, columnData)
    };
  }
}
